import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useApi';
import { apiClient } from '../services/api';
import LoadingOverlay from './LoadingOverlay';

interface UserProfileDropdownProps {
  user: {
    email?: string;
    user_metadata?: {
      avatar_url?: string;
      full_name?: string;
    };
  };
}

export default function UserProfileDropdown({ user }: UserProfileDropdownProps) {
  const { signOut, user: authUser } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user: userData } = useUser();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const avatarUrl = user.user_metadata?.avatar_url;
  const displayName = user.user_metadata?.full_name || user.email;
  
  // Check if user has an active or canceling subscription
  const hasActiveSubscription = userData?.subscription_status === 'active' || userData?.subscription_status === 'canceling';
  
  // Open Stripe Customer Portal (no email login needed!)
  const openStripePortal = async () => {
    if (!authUser?.id) {
      console.error('❌ [UserProfileDropdown] No user ID available');
      alert('Unable to open billing portal. Please try refreshing the page.');
      return;
    }

    try {
      setIsLoadingPortal(true);
      
      // Create portal session via API
      const { url } = await apiClient.createPortalSession(
        authUser.id,
        `${window.location.origin}/my-collection`
      );
      
      // Redirect to Stripe portal (no email needed!)
      window.location.href = url;
    } catch (error) {
      console.error('❌ [UserProfileDropdown] Failed to open billing portal:', error);
      
      // Close overlay first
      setIsLoadingPortal(false);
      
      // Show error after a brief delay to ensure overlay closes
      setTimeout(() => {
        const errorMessage = error instanceof Error ? error.message : 'Unable to open billing portal. Please try again.';
        alert(`${errorMessage}\n\nNote: If you're on dev environment, make sure you have a test subscription created with dev's test Stripe keys.`);
      }, 100);
    }
  };
  
  const handleManageSubscription = () => {
    setIsOpen(false);
    openStripePortal();
  };
  
  const handleUpgradeClick = () => {
    setIsOpen(false);
    if (hasActiveSubscription) {
      // If user has active or canceling subscription, open Stripe billing portal
      openStripePortal();
    } else {
      // Otherwise, navigate to upgrade page
      navigate('/upgrade');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity"
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={user.email || 'User'}
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <span className="text-white text-sm font-semibold">
              {displayName?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
        )}
        <span className="hidden md:block">{displayName}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 bg-bg-panel border border-border rounded-card shadow-lg min-w-[200px] z-50 overflow-hidden">
          <button
            onClick={() => {
              navigate('/my-collection');
              setIsOpen(false);
            }}
            className="w-full text-left px-4 py-3 min-h-[44px] text-white hover:bg-hover transition-colors text-body"
          >
            My Collection
          </button>
          <button
            onClick={handleUpgradeClick}
            className="w-full text-left px-4 py-3 min-h-[44px] text-white hover:bg-hover transition-colors text-body"
          >
            {hasActiveSubscription ? 'Manage Subscription' : 'Upgrade'}
          </button>
          <button
            onClick={handleSignOut}
            className="w-full text-left px-4 py-3 min-h-[44px] text-red-400 hover:bg-hover transition-colors text-body"
          >
            Sign Out
          </button>
        </div>
      )}

      {/* Loading overlay for portal session */}
      <LoadingOverlay
        show={isLoadingPortal}
        message="Opening Billing Portal..."
      />
    </div>
  );
}

